/**
 * Settings Store
 * Global settings state synchronized with localStorage
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ProviderId } from '@/lib/ai/providers';
import type { ProvidersConfig } from '@/lib/types/settings';
import { PROVIDERS } from '@/lib/ai/providers';
import type { ThinkingConfig } from '@/lib/types/provider';
import { getThinkingConfigKey, supportsConfigurableThinking } from '@/lib/ai/thinking-config';
import type { TTSProviderId, ASRProviderId, BuiltInTTSProviderId } from '@/lib/audio/types';
import { isCustomTTSProvider, isCustomASRProvider } from '@/lib/audio/types';
import { ASR_PROVIDERS, DEFAULT_TTS_VOICES, TTS_PROVIDERS } from '@/lib/audio/constants';
import { DEFAULT_VOXCPM_BACKEND, VOXCPM_MODEL_ID, VOXCPM_VLLM_MODEL_ID } from '@/lib/audio/voxcpm';
import { PDF_PROVIDERS } from '@/lib/pdf/constants';
import type { PDFProviderId } from '@/lib/pdf/types';
import type { ImageProviderId, VideoProviderId } from '@/lib/media/types';
import { IMAGE_PROVIDERS } from '@/lib/media/image-providers';
import { VIDEO_PROVIDERS } from '@/lib/media/video-providers';
import { WEB_SEARCH_PROVIDERS } from '@/lib/web-search/constants';
import type { WebSearchProviderId } from '@/lib/web-search/types';
import { createLogger } from '@/lib/logger';
import { validateProvider, validateModel } from '@/lib/store/settings-validation';

/**
 * Get the list of valid voice IDs for a TTS provider.
 *
 * Per spec: a user-selected voice is only honored when its ID exists in the
 * current default TTS provider's voice list (built-in `DEFAULT_TTS_VOICES` or
 * custom-voice map from `ttsProvidersConfig`). Otherwise the selection is
 * treated as if the user hadn't picked anything and the role default applies.
 */
function getValidVoicesForProvider(
  providerId: TTSProviderId,
  ttsProvidersConfig: Record<string, { customVoices?: Array<{ id: string; name?: string }> }>,
): string[] {
  if (!providerId) return [];
  if (isCustomTTSProvider(providerId)) {
    const custom = ttsProvidersConfig?.[providerId]?.customVoices;
    if (Array.isArray(custom)) {
      return custom.map((v) => v.id).filter((id): id is string => typeof id === 'string' && id.length > 0);
    }
    return [];
  }
  // Built-in provider: there's a single default voice per provider in
  // DEFAULT_TTS_VOICES, but the actual voice list comes from admin customVoices
  // when the user has configured extras. We always include both: the built-in
  // default + any customVoices admin defined for this provider.
  const builtInDefault = DEFAULT_TTS_VOICES[providerId as BuiltInTTSProviderId];
  const adminCustom = ttsProvidersConfig?.[providerId]?.customVoices;
  const ids: string[] = [];
  if (builtInDefault) ids.push(builtInDefault);
  if (Array.isArray(adminCustom)) {
    for (const v of adminCustom) {
      if (v?.id) ids.push(v.id);
    }
  }
  // 'default' is always accepted as a graceful fallback (browser-native TTS)
  if (providerId === 'browser-native-tts') ids.push('default');
  return Array.from(new Set(ids));
}

const log = createLogger('Settings');

// 【v6.97.50.3 P0 FIX 2026-07-18】fetchAdminDefaults 模块级 dedupe 状态
// 解决老板 P0：浏览器 40ms 内连发 40 次 /api/config/defaults 把主线程打满，"进入课堂" 卡死
// - inFlightAdminDefaultsPromise: 同一时刻所有 caller 共享同一 Promise（避免 mount storm）
// - lastAdminDefaultsSuccessAt: 成功后 1.5s 内重复调用直接 return（避免 polling 抖动重发）
// - lastAdminDefaultsFailureAt: 失败后 3s 内重复调用直接 throw（让 useConfigVersionPolling 退避生效）
let inFlightAdminDefaultsPromise: Promise<void> | null = null;
let lastAdminDefaultsSuccessAt = 0;
let lastAdminDefaultsFailureAt = 0;
const ADMIN_DEFAULTS_MIN_INTERVAL_MS = 1500;
const ADMIN_DEFAULTS_FAILURE_BACKOFF_MS = 3000;

function pruneThinkingConfigs(
  thinkingConfigs: Record<string, ThinkingConfig> | undefined,
  providersConfig: ProvidersConfig | undefined,
): Record<string, ThinkingConfig> {
  if (!thinkingConfigs || !providersConfig) return {};

  const validKeys = new Set<string>();
  for (const [providerId, providerConfig] of Object.entries(providersConfig)) {
    for (const model of providerConfig.models) {
      if (supportsConfigurableThinking(model.capabilities?.thinking)) {
        validKeys.add(getThinkingConfigKey(providerId, model.id));
      }
    }
  }

  return Object.fromEntries(
    Object.entries(thinkingConfigs).filter(([key]) => validKeys.has(key)),
  ) as Record<string, ThinkingConfig>;
}

/** Available playback speed tiers */
export const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2] as const;
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

export interface SettingsState {
  // Model selection
  providerId: ProviderId;
  modelId: string;
  thinkingConfigs: Record<string, ThinkingConfig>;

  // Provider configurations (unified JSON storage)
  providersConfig: ProvidersConfig;

  // TTS settings (legacy, kept for backward compatibility)
  ttsModel: string;

  // Audio settings (new unified audio configuration)
  ttsProviderId: TTSProviderId;
  ttsVoice: string;
  ttsSpeed: number;
  asrProviderId: ASRProviderId;
  asrLanguage: string;
  // 【2026-06-12 新增】Web Speech API 永久 fallback 标记（cross-mount 持久化到 zustand）
  // 触发场景：浏览器到 Google 服务器连接失败（没翻墙/firewall/Chrome 禁用 Google）
  // 一旦设为 true，所有录音都走 server ASR（fetch /api/transcription），
  // 不会再尝试 Web Speech API。
  asrBrowserFallbackUsed: boolean;

  // Audio provider configurations
  ttsProvidersConfig: Record<
    TTSProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      modelId?: string;
      customModels?: Array<{ id: string; name: string }>;
      providerOptions?: Record<string, unknown>;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
      // Custom provider fields
      customName?: string;
      customDefaultBaseUrl?: string;
      customVoices?: Array<{ id: string; name: string }>;
      isBuiltIn?: boolean;
      requiresApiKey?: boolean;
    }
  >;

  asrProvidersConfig: Record<
    ASRProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      modelId?: string;
      customModels?: Array<{ id: string; name: string }>;
      providerOptions?: Record<string, unknown>;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
      // Custom provider fields
      customName?: string;
      customDefaultBaseUrl?: string;
      isBuiltIn?: boolean;
      requiresApiKey?: boolean;
    }
  >;

  // PDF settings
  pdfProviderId: PDFProviderId;
  pdfProvidersConfig: Record<
    PDFProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
      /**
       * AliDocMind (Aliyun DocMind) uses AK/SK instead of a single apiKey.
       * Additive extension — prod's existing providers ignore these fields.
       * 【2026-07-28 决策点 1.5 vendor】(补 commit 6d3adc6e 漏的 pdfProvidersConfig 字段类型)
       */
      accessKeyId?: string;
      accessKeySecret?: string;
    }
  >;

  // Image Generation settings
  imageProviderId: ImageProviderId;
  imageModelId: string;
  imageProvidersConfig: Record<
    ImageProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
      customModels?: Array<{ id: string; name: string }>;
    }
  >;

  // Video Generation settings
  videoProviderId: VideoProviderId;
  videoModelId: string;
  videoProvidersConfig: Record<
    VideoProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
      customModels?: Array<{ id: string; name: string }>;
    }
  >;

  // Media generation toggles
  imageGenerationEnabled: boolean;
  videoGenerationEnabled: boolean;

  /**
   * 【v0.x — 2026-07-12】Hard-lock from FastAdmin 后台「图片生成」功能开关 (fa_dongniai_feature.image_generation).
   * Defaults to `true` (fail-open) so existing behavior is preserved when:
   *   - admin 默认值无法获取 (网络失败 / DB down)
   *   - admin feature row 缺失 (install 时还没建)
   * 当 admin lock = false:
   *   - `imageGenerationEnabled` 被强制为 false (即使 user 之前 toggle 过 true)
   *   - `setImageGenerationEnabled(true)` 被 silently 拒绝
   *   - LLM prompt 不输出 mediaGenerations image 项 (`{{#if mediaEnabled}}` 条件)
   *   - media-orchestrator 跳过所有 type='image' 任务
   * 见配套改动: outline-generator.ts applyOutlineFallbacks() filter image + slide 渲染 no-src 跳过
   */
  adminImageGenerationEnabled: boolean;

  // Web Search settings
  webSearchProviderId: WebSearchProviderId;
  webSearchProvidersConfig: Record<
    WebSearchProviderId,
    {
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      isServerConfigured?: boolean;
      serverBaseUrl?: string;
    }
  >;

  // Global TTS/ASR toggles
  ttsEnabled: boolean;
  asrEnabled: boolean;

  // Auto-config lifecycle flag (persisted)
  autoConfigApplied: boolean;

  // Playback controls
  ttsMuted: boolean;
  ttsVolume: number; // 0-1, actual volume level
  autoPlayLecture: boolean;
  playbackSpeed: PlaybackSpeed;

  // Agent settings
  selectedAgentIds: string[];
  maxTurns: string;
  agentMode: 'preset' | 'auto';
  autoAgentCount: number;

  // Layout preferences (persisted via localStorage)
  sidebarCollapsed: boolean;
  chatAreaCollapsed: boolean;
  chatAreaWidth: number;

  // Actions
  setModel: (providerId: ProviderId, modelId: string) => void;
  setThinkingConfig: (
    providerId: ProviderId,
    modelId: string,
    config: ThinkingConfig | undefined,
  ) => void;
  setProviderConfig: (providerId: ProviderId, config: Partial<ProvidersConfig[ProviderId]>) => void;
  setProvidersConfig: (config: ProvidersConfig) => void;
  setTtsModel: (model: string) => void;
  setTTSMuted: (muted: boolean) => void;
  setTTSVolume: (volume: number) => void;
  setAutoPlayLecture: (autoPlay: boolean) => void;
  setPlaybackSpeed: (speed: PlaybackSpeed) => void;
  setSelectedAgentIds: (ids: string[]) => void;
  setMaxTurns: (turns: string) => void;
  setAgentMode: (mode: 'preset' | 'auto') => void;
  setAutoAgentCount: (count: number) => void;

  // Layout actions
  setSidebarCollapsed: (collapsed: boolean) => void;
  setChatAreaCollapsed: (collapsed: boolean) => void;
  setChatAreaWidth: (width: number) => void;

  // 【v0.3.0 兼容 dongniai】MAIC Editor 工具栏折叠
  editInsertToolbarCollapsed: boolean;
  setEditInsertToolbarCollapsed: (collapsed: boolean) => void;
  // 【v0.3.0 兼容 dongniai】SlideNavRail editRailCollapsed
  editRailCollapsed: boolean;
  setEditRailCollapsed: (collapsed: boolean) => void;
  // 【v0.3.0 兼容 dongniai】SlideNavRail editRailWidth
  editRailWidth: number;
  setEditRailWidth: (width: number) => void;

  // Audio actions
  setTTSProvider: (providerId: TTSProviderId) => void;
  setTTSVoice: (voice: string) => void;
  userSelectedVoice: boolean; // true if user manually changed voice in settings
  setTTSSpeed: (speed: number) => void;
  setASRProvider: (providerId: ASRProviderId) => void;
  setASRLanguage: (language: string) => void;
  setTTSProviderConfig: (
    providerId: TTSProviderId,
    config: Partial<{
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      modelId: string;
      customModels: Array<{ id: string; name: string }>;
      customVoices: Array<{ id: string; name: string }>;
      providerOptions: Record<string, unknown>;
    }>,
  ) => void;
  setASRProviderConfig: (
    providerId: ASRProviderId,
    config: Partial<{
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      modelId: string;
      customModels: Array<{ id: string; name: string }>;
      providerOptions: Record<string, unknown>;
    }>,
  ) => void;
  setTTSEnabled: (enabled: boolean) => void;
  setASREnabled: (enabled: boolean) => void;

  // Custom audio provider actions
  addCustomTTSProvider: (
    id: TTSProviderId,
    name: string,
    baseUrl: string,
    requiresApiKey: boolean,
    defaultModel?: string,
  ) => void;
  removeCustomTTSProvider: (id: TTSProviderId) => void;
  addCustomASRProvider: (
    id: ASRProviderId,
    name: string,
    baseUrl: string,
    requiresApiKey: boolean,
  ) => void;
  removeCustomASRProvider: (id: ASRProviderId) => void;

  // PDF actions
  setPDFProvider: (providerId: PDFProviderId) => void;
  setPDFProviderConfig: (
    providerId: PDFProviderId,
    config: Partial<{
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      /**
       * AliDocMind (Aliyun DocMind) uses AK/SK instead of a single apiKey.
       * Additive extension — prod's existing providers ignore these fields.
       * 【2026-07-28 决策点 1.5 vendor】
       */
      accessKeyId?: string;
      accessKeySecret?: string;
    }>,
  ) => void;

  // Image Generation actions
  setImageProvider: (providerId: ImageProviderId) => void;
  setImageModelId: (modelId: string) => void;
  setImageProviderConfig: (
    providerId: ImageProviderId,
    config: Partial<{
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      customModels: Array<{ id: string; name: string }>;
    }>,
  ) => void;

  // Video Generation actions
  setVideoProvider: (providerId: VideoProviderId) => void;
  setVideoModelId: (modelId: string) => void;
  setVideoProviderConfig: (
    providerId: VideoProviderId,
    config: Partial<{
      apiKey: string;
      baseUrl: string;
      enabled: boolean;
      customModels: Array<{ id: string; name: string }>;
    }>,
  ) => void;

  // Media generation toggle actions
  setImageGenerationEnabled: (enabled: boolean) => void;
  setVideoGenerationEnabled: (enabled: boolean) => void;

  // Web Search actions
  setWebSearchProvider: (providerId: WebSearchProviderId) => void;
  setWebSearchProviderConfig: (
    providerId: WebSearchProviderId,
    config: Partial<{ apiKey: string; baseUrl: string; enabled: boolean }>,
  ) => void;

  // Server provider actions
  fetchServerProviders: () => Promise<void>;
  /**
   * 老板 P0 2026-09-04：支持 force=true 跳过 1500ms 成功 throttle，
   * 让 useConfigVersionPolling 在版本变化的下一次 1 秒 tick 立刻拿到新配置。
   * 普通 mount 路径仍走默认（force=false），in-flight 仍共享同一个 Promise，不会雪崩。
   */
  fetchAdminDefaults: (force?: boolean) => Promise<void>;
}

// Initialize default providers config
const getDefaultProvidersConfig = (): ProvidersConfig => {
  const config: ProvidersConfig = {} as ProvidersConfig;
  Object.keys(PROVIDERS).forEach((pid) => {
    const provider = PROVIDERS[pid as ProviderId];
    config[pid as ProviderId] = {
      apiKey: '',
      baseUrl: '',
      models: provider.models,
      name: provider.name,
      type: provider.type,
      defaultBaseUrl: provider.defaultBaseUrl,
      icon: provider.icon,
      requiresApiKey: provider.requiresApiKey,
      isBuiltIn: true,
    };
  });
  return config;
};

// Initialize default audio config
const getDefaultAudioConfig = () => ({
  ttsProviderId: 'browser-native-tts' as TTSProviderId,
  ttsVoice: 'default',
  ttsSpeed: 1.0,
  asrProviderId: 'browser-native' as ASRProviderId,
  asrLanguage: 'zh',
  // 【2026-06-12 新增】默认 false —— 只有 Web Speech API 报 network 错时
  // use-audio-recorder.ts 的 onerror 'network' case 会 setState(true) 持久化。
  asrBrowserFallbackUsed: false,
  ttsProvidersConfig: {
    'openai-tts': { apiKey: '', baseUrl: '', enabled: true },
    'azure-tts': { apiKey: '', baseUrl: '', enabled: false },
    'glm-tts': { apiKey: '', baseUrl: '', enabled: false },
    'qwen-tts': { apiKey: '', baseUrl: '', enabled: false },
    'voxcpm-tts': {
      apiKey: '',
      baseUrl: '',
      modelId: VOXCPM_VLLM_MODEL_ID,
      enabled: false,
      providerOptions: { backend: DEFAULT_VOXCPM_BACKEND },
    },
    'doubao-tts': { apiKey: '', baseUrl: '', enabled: false },
    'elevenlabs-tts': { apiKey: '', baseUrl: '', enabled: false },
    'minimax-tts': { apiKey: '', baseUrl: '', modelId: 'speech-2.8-hd', enabled: false },
    'browser-native-tts': { apiKey: '', baseUrl: '', enabled: true },
    'lemonade-tts': { apiKey: '', baseUrl: '', enabled: false },
    'xiaomimimo-tts': { apiKey: '', baseUrl: '', enabled: false },
  } as Record<
    TTSProviderId,
    { apiKey: string; baseUrl: string; modelId?: string; enabled: boolean }
  >,
  asrProvidersConfig: {
    'openai-whisper': { apiKey: '', baseUrl: '', enabled: true },
    'browser-native': { apiKey: '', baseUrl: '', enabled: true },
    'qwen-asr': { apiKey: '', baseUrl: '', enabled: false },
  } as Record<ASRProviderId, { apiKey: string; baseUrl: string; enabled: boolean }>,
});

// Initialize default PDF config
const getDefaultPDFConfig = () => ({
  pdfProviderId: 'unpdf' as PDFProviderId,
  pdfProvidersConfig: {
    unpdf: { apiKey: '', baseUrl: '', enabled: true },
    mineru: { apiKey: '', baseUrl: '', enabled: false },
    'mineru-cloud': { apiKey: '', baseUrl: '', enabled: false },
    // 【2026-07-29 P0 FIX】阿里云 AliDocMind 必须注册到默认 cfg
    //                 之前没注册 → store 里查不到 alidocmind entry → accessKeyId/Secret 永远 undefined
    //                 AK/SK 透传跟 provider 完全无关, 因为 store 是按 pdfProviderId 索引的
    alidocmind: { apiKey: '', baseUrl: '', enabled: false, accessKeyId: '', accessKeySecret: '' },
  } as Record<PDFProviderId, { apiKey: string; baseUrl: string; enabled: boolean; accessKeyId?: string; accessKeySecret?: string }>,
});

// Initialize default Image config
const getDefaultImageConfig = () => ({
  imageProviderId: 'seedream' as ImageProviderId,
  imageModelId: 'doubao-seedream-5-0-260128',
  imageProvidersConfig: {
    seedream: { apiKey: '', baseUrl: '', enabled: false },
    'openai-image': { apiKey: '', baseUrl: '', enabled: false },
    'qwen-image': { apiKey: '', baseUrl: '', enabled: false },
    'nano-banana': { apiKey: '', baseUrl: '', enabled: false },
    'minimax-image': { apiKey: '', baseUrl: '', enabled: false },
    'grok-image': { apiKey: '', baseUrl: '', enabled: false },
  } as Record<ImageProviderId, { apiKey: string; baseUrl: string; enabled: boolean }>,
});

// Initialize default Video config
const getDefaultVideoConfig = () => ({
  videoProviderId: 'seedance' as VideoProviderId,
  videoModelId: 'doubao-seedance-1-5-pro-251215',
  videoProvidersConfig: {
    seedance: { apiKey: '', baseUrl: '', enabled: false },
    kling: { apiKey: '', baseUrl: '', enabled: false },
    veo: { apiKey: '', baseUrl: '', enabled: false },
    sora: { apiKey: '', baseUrl: '', enabled: false },
    'minimax-video': { apiKey: '', baseUrl: '', enabled: false },
    'grok-video': { apiKey: '', baseUrl: '', enabled: false },
  } as Record<VideoProviderId, { apiKey: string; baseUrl: string; enabled: boolean }>,
});

// Initialize default Web Search config
const getDefaultWebSearchConfig = () => ({
  webSearchProviderId: 'tavily' as WebSearchProviderId,
  webSearchProvidersConfig: {
    tavily: { apiKey: '', baseUrl: '', enabled: true },
    minimax: { apiKey: '', baseUrl: '', enabled: false },
  } as Record<WebSearchProviderId, { apiKey: string; baseUrl: string; enabled: boolean }>,
});

/**
 * Check whether a provider ID exists in the given provider registry.
 */
function hasProviderId(providerMap: Record<string, unknown>, providerId?: string): boolean {
  return typeof providerId === 'string' && providerId in providerMap;
}

/**
 * Validate all persisted provider IDs against their registries.
 * Reset any stale / removed ID back to its default value.
 * Called during both migrate and merge to cover all rehydration paths.
 */
function ensureValidProviderSelections(state: Partial<SettingsState>): void {
  const defaultAudioConfig = getDefaultAudioConfig();
  const defaultPdfConfig = getDefaultPDFConfig();
  const defaultImageConfig = getDefaultImageConfig();
  const defaultVideoConfig = getDefaultVideoConfig();
  const defaultWebSearchConfig = getDefaultWebSearchConfig();

  if (!hasProviderId(PDF_PROVIDERS, state.pdfProviderId)) {
    state.pdfProviderId = defaultPdfConfig.pdfProviderId;
  }

  if (!hasProviderId(WEB_SEARCH_PROVIDERS, state.webSearchProviderId)) {
    state.webSearchProviderId = defaultWebSearchConfig.webSearchProviderId;
  }

  if (!hasProviderId(IMAGE_PROVIDERS, state.imageProviderId)) {
    state.imageProviderId = defaultImageConfig.imageProviderId;
  }

  if (!hasProviderId(VIDEO_PROVIDERS, state.videoProviderId)) {
    state.videoProviderId = defaultVideoConfig.videoProviderId;
  }

  if (
    !hasProviderId(TTS_PROVIDERS, state.ttsProviderId) &&
    !(
      state.ttsProviderId &&
      isCustomTTSProvider(state.ttsProviderId) &&
      state.ttsProvidersConfig &&
      state.ttsProviderId in state.ttsProvidersConfig
    )
  ) {
    state.ttsProviderId = defaultAudioConfig.ttsProviderId;
  }

  if (
    !hasProviderId(ASR_PROVIDERS, state.asrProviderId) &&
    !(
      state.asrProviderId &&
      isCustomASRProvider(state.asrProviderId) &&
      state.asrProvidersConfig &&
      state.asrProviderId in state.asrProvidersConfig
    )
  ) {
    state.asrProviderId = defaultAudioConfig.asrProviderId;
  }
}

function ensureBuiltInAudioProviders(state: Partial<SettingsState>): void {
  const defaultAudioConfig = getDefaultAudioConfig();

  if (state.ttsProvidersConfig) {
    for (const providerId of Object.keys(TTS_PROVIDERS) as BuiltInTTSProviderId[]) {
      if (!state.ttsProvidersConfig[providerId]) {
        state.ttsProvidersConfig[providerId] = defaultAudioConfig.ttsProvidersConfig[providerId];
      }
    }
    const voxcpmConfig = state.ttsProvidersConfig['voxcpm-tts'];
    if (voxcpmConfig) {
      if (!voxcpmConfig.modelId || voxcpmConfig.modelId === VOXCPM_MODEL_ID) {
        voxcpmConfig.modelId = VOXCPM_VLLM_MODEL_ID;
      }
      voxcpmConfig.providerOptions = {
        backend: DEFAULT_VOXCPM_BACKEND,
        ...(voxcpmConfig.providerOptions || {}),
      };
    }
  }

  if (state.asrProvidersConfig) {
    for (const providerId of Object.keys(ASR_PROVIDERS) as ASRProviderId[]) {
      if (!state.asrProvidersConfig[providerId]) {
        state.asrProvidersConfig[providerId] = defaultAudioConfig.asrProvidersConfig[providerId];
      }
    }
  }
}

/**
 * Ensure providersConfig includes all built-in providers and their latest models.
 * Called on every rehydrate (not just version migrations) so new providers
 * added in code are always picked up without clearing cache.
 */
function ensureBuiltInProviders(state: Partial<SettingsState>): void {
  if (!state.providersConfig) return;
  const defaultConfig = getDefaultProvidersConfig();
  Object.keys(PROVIDERS).forEach((pid) => {
    const providerId = pid as ProviderId;
    if (!state.providersConfig![providerId]) {
      // New provider: add with defaults
      state.providersConfig![providerId] = defaultConfig[providerId];
    } else {
      // Existing provider: refresh built-in models from the registry and
      // keep user-added models after the built-in list.
      const provider = PROVIDERS[providerId];
      const existing = state.providersConfig![providerId];

      const builtInModelIds = new Set(provider.models.map((m) => m.id));
      const customModels = (existing.models || []).filter((m) => !builtInModelIds.has(m.id));
      const mergedModels = [...provider.models, ...customModels];

      state.providersConfig![providerId] = {
        ...existing,
        models: mergedModels,
        name: existing.name || provider.name,
        type: existing.type || provider.type,
        defaultBaseUrl: existing.defaultBaseUrl || provider.defaultBaseUrl,
        icon: provider.icon || existing.icon,
        requiresApiKey: existing.requiresApiKey ?? provider.requiresApiKey,
        isBuiltIn: existing.isBuiltIn ?? true,
      };
    }
  });
}

/**
 * Custom providers created before #414 stored their actual endpoint in
 * defaultBaseUrl while leaving baseUrl empty. Promote that persisted value
 * during rehydrate so downstream request builders keep using baseUrl only.
 */
export function promoteLegacyCustomProviderBaseUrls(state: Partial<SettingsState>): void {
  if (!state.providersConfig) return;

  Object.values(state.providersConfig).forEach((config) => {
    if (!config.isBuiltIn && !config.baseUrl && config.defaultBaseUrl) {
      config.baseUrl = config.defaultBaseUrl;
    }
  });
}

/**
 * Ensure imageProvidersConfig includes all built-in image providers.
 * Called on every rehydrate so newly added image providers appear automatically.
 */
function ensureBuiltInImageProviders(state: Partial<SettingsState>): void {
  if (!state.imageProvidersConfig) return;
  const defaultConfig = getDefaultImageConfig().imageProvidersConfig;
  Object.keys(IMAGE_PROVIDERS).forEach((pid) => {
    const providerId = pid as ImageProviderId;
    if (!state.imageProvidersConfig![providerId]) {
      state.imageProvidersConfig![providerId] = defaultConfig[providerId];
    }
  });
}

/**
 * Ensure videoProvidersConfig includes all built-in video providers.
 * Called on every rehydrate so newly added video providers appear automatically.
 */
function ensureBuiltInVideoProviders(state: Partial<SettingsState>): void {
  if (!state.videoProvidersConfig) return;
  const defaultConfig = getDefaultVideoConfig().videoProvidersConfig;
  Object.keys(VIDEO_PROVIDERS).forEach((pid) => {
    const providerId = pid as VideoProviderId;
    if (!state.videoProvidersConfig![providerId]) {
      state.videoProvidersConfig![providerId] = defaultConfig[providerId];
    }
  });
}

// Migrate from old localStorage format
const migrateFromOldStorage = () => {
  if (typeof window === 'undefined') return null;

  // Check if new storage already exists
  const newStorage = localStorage.getItem('settings-storage');
  if (newStorage) return null; // Already migrated or new install

  // Read old localStorage keys
  const oldLlmModel = localStorage.getItem('llmModel');
  const oldProvidersConfig = localStorage.getItem('providersConfig');
  const oldTtsModel = localStorage.getItem('ttsModel');
  const oldSelectedAgents = localStorage.getItem('selectedAgentIds');
  const oldMaxTurns = localStorage.getItem('maxTurns');

  if (!oldLlmModel && !oldProvidersConfig) return null; // No old data

  // Parse model selection
  let providerId: ProviderId = 'openai';
  let modelId = 'gpt-5.4-mini';
  if (oldLlmModel) {
    const [pid, mid] = oldLlmModel.split(':');
    if (pid && mid) {
      providerId = pid as ProviderId;
      modelId = mid;
    }
  }

  // Parse providers config
  let providersConfig = getDefaultProvidersConfig();
  if (oldProvidersConfig) {
    try {
      const parsed = JSON.parse(oldProvidersConfig);
      providersConfig = { ...providersConfig, ...parsed };
    } catch (e) {
      log.error('Failed to parse old providersConfig:', e);
    }
  }

  // Parse other settings
  let ttsModel = 'openai-tts';
  if (oldTtsModel) ttsModel = oldTtsModel;

  let selectedAgentIds = ['default-1'];
  if (oldSelectedAgents) {
    try {
      const parsed = JSON.parse(oldSelectedAgents);
      if (Array.isArray(parsed) && parsed.length > 0) {
        selectedAgentIds = parsed;
      }
    } catch (e) {
      log.error('Failed to parse old selectedAgentIds:', e);
    }
  }

  let maxTurns = '10';
  if (oldMaxTurns) maxTurns = oldMaxTurns;

  return {
    providerId,
    modelId,
    thinkingConfigs: {},
    providersConfig,
    ttsModel,
    selectedAgentIds,
    maxTurns,
  };
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => {
      // Try to migrate from old storage
      const migratedData = migrateFromOldStorage();
      const defaultAudioConfig = getDefaultAudioConfig();
      const defaultPDFConfig = getDefaultPDFConfig();
      const defaultImageConfig = getDefaultImageConfig();
      const defaultVideoConfig = getDefaultVideoConfig();
      const defaultWebSearchConfig = getDefaultWebSearchConfig();

      const initialProvidersConfig = migratedData?.providersConfig || getDefaultProvidersConfig();

      return {
        // Initial state (use migrated data if available)
        providerId: migratedData?.providerId || 'openai',
        modelId: migratedData?.modelId || '',
        thinkingConfigs: pruneThinkingConfigs(
          migratedData?.thinkingConfigs || {},
          initialProvidersConfig,
        ),
        providersConfig: initialProvidersConfig,
        ttsModel: migratedData?.ttsModel || 'openai-tts',
        selectedAgentIds: migratedData?.selectedAgentIds || ['default-1'],
        maxTurns: migratedData?.maxTurns?.toString() || '10',
        agentMode: 'auto' as const,
        autoAgentCount: 3,

        // Playback controls
        ttsMuted: false,
        ttsVolume: 1,
        autoPlayLecture: true,
        playbackSpeed: 1,

        // Layout preferences
        sidebarCollapsed: true,
        chatAreaCollapsed: true,
        chatAreaWidth: 320,
        // 【v0.3.0 兼容 dongniai】MAIC Editor 工具栏折叠默认收起
        editInsertToolbarCollapsed: false,
        // 【v0.3.0 兼容 dongniai】SlideNavRail editRailCollapsed
        editRailCollapsed: false,
        // 【v0.3.0 兼容 dongniai】SlideNavRail editRailWidth
        editRailWidth: 240,

        // Audio settings (use defaults)
        ...defaultAudioConfig,

        // PDF settings (use defaults)
        ...defaultPDFConfig,

        // Image settings (use defaults)
        ...defaultImageConfig,

        // Video settings (use defaults)
        ...defaultVideoConfig,

        // Media generation toggles (off by default)
        imageGenerationEnabled: false,
        videoGenerationEnabled: false,

        // 【v0.x — 2026-07-12】后台「图片生成」hard-lock (fail-open: true). 详见 interface 注释.
        adminImageGenerationEnabled: true,

        // Audio feature toggles (on by default)
        ttsEnabled: true,
        asrEnabled: true,

        autoConfigApplied: false,
        userSelectedVoice: false,

        // Web Search settings (use defaults)
        ...defaultWebSearchConfig,

        // Actions
        setModel: (providerId, modelId) => set({ providerId, modelId }),

        setThinkingConfig: (providerId, modelId, config) =>
          set((state) => {
            const key = getThinkingConfigKey(providerId, modelId);
            const next = { ...state.thinkingConfigs };
            if (config) {
              next[key] = config;
            } else {
              delete next[key];
            }
            return { thinkingConfigs: next };
          }),

        setProviderConfig: (providerId, config) =>
          set((state) => {
            const providersConfig = {
              ...state.providersConfig,
              [providerId]: {
                ...state.providersConfig[providerId],
                ...config,
              },
            };
            return {
              providersConfig,
              thinkingConfigs: pruneThinkingConfigs(state.thinkingConfigs, providersConfig),
            };
          }),

        setProvidersConfig: (config) =>
          set((state) => ({
            providersConfig: config,
            thinkingConfigs: pruneThinkingConfigs(state.thinkingConfigs, config),
          })),

        setTtsModel: (model) => set({ ttsModel: model }),

        setTTSMuted: (muted) => set({ ttsMuted: muted }),

        setTTSVolume: (volume) => set({ ttsVolume: Math.max(0, Math.min(1, volume)) }),

        setAutoPlayLecture: (autoPlay) => set({ autoPlayLecture: autoPlay }),

        setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),

        setSelectedAgentIds: (ids) => set({ selectedAgentIds: ids }),

        setMaxTurns: (turns) => set({ maxTurns: turns }),
        setAgentMode: (mode) => set({ agentMode: mode }),
        setAutoAgentCount: (count) => set({ autoAgentCount: count }),

        // Layout actions
        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
        setChatAreaCollapsed: (collapsed) => set({ chatAreaCollapsed: collapsed }),
        setChatAreaWidth: (width) => set({ chatAreaWidth: width }),
        // 【v0.3.0 兼容 dongniai】MAIC Editor 工具栏折叠 action
        setEditInsertToolbarCollapsed: (collapsed) => set({ editInsertToolbarCollapsed: collapsed }),
        // 【v0.3.0 兼容 dongniai】SlideNavRail editRailCollapsed action
        setEditRailCollapsed: (collapsed) => set({ editRailCollapsed: collapsed }),
        // 【v0.3.0 兼容 dongniai】SlideNavRail editRailWidth action
        setEditRailWidth: (width) => set({ editRailWidth: width }),

        // Audio actions
        setTTSProvider: (providerId) =>
          set((state) => {
            // If switching provider, set default voice for that provider
            const shouldUpdateVoice = state.ttsProviderId !== providerId;
            const defaultVoice = isCustomTTSProvider(providerId)
              ? state.ttsProvidersConfig[providerId]?.customVoices?.[0]?.id || 'default'
              : DEFAULT_TTS_VOICES[providerId as BuiltInTTSProviderId] || 'default';
            return {
              ttsProviderId: providerId,
              ...(shouldUpdateVoice && { ttsVoice: defaultVoice }),
            };
          }),

        setTTSVoice: (voice) =>
          set((state) => {
            // Per spec: only honor the user's selection if `voice` is a valid
            // voice ID for the current TTS provider. Otherwise treat it as
            // "user hasn't picked anything" so the role default (set in
            // fetchAdminDefaults → finalVoice) wins.
            const validVoices = getValidVoicesForProvider(
              state.ttsProviderId,
              state.ttsProvidersConfig as Record<string, { customVoices?: Array<{ id: string; name?: string }> }>,
            );
            const isValid = validVoices.includes(voice);
            if (!isValid) {
              log.warn(
                `[setTTSVoice] Voice "${voice}" is not in valid list for provider "${state.ttsProviderId}". Valid:`,
                validVoices,
                '— selection will be ignored, role default will be used.',
              );
              return { ttsVoice: voice, userSelectedVoice: false };
            }
            return { ttsVoice: voice, userSelectedVoice: true };
          }),

        setTTSSpeed: (speed) => set({ ttsSpeed: speed }),

        // Reset language when switching providers, since language code formats differ
        // (e.g. browser-native uses BCP-47 "en-US", OpenAI Whisper uses ISO 639-1 "en")
        setASRProvider: (providerId) =>
          set((state) => {
            let supportedLanguages: string[];
            if (isCustomASRProvider(providerId)) {
              supportedLanguages = ['auto'];
            } else {
              supportedLanguages =
                ASR_PROVIDERS[providerId as keyof typeof ASR_PROVIDERS]?.supportedLanguages || [];
            }
            const isLanguageValid = supportedLanguages.includes(state.asrLanguage);
            return {
              asrProviderId: providerId,
              ...(isLanguageValid ? {} : { asrLanguage: supportedLanguages[0] || 'auto' }),
            };
          }),

        setASRLanguage: (language) => set({ asrLanguage: language }),

        setTTSProviderConfig: (providerId, config) =>
          set((state) => ({
            ttsProvidersConfig: {
              ...state.ttsProvidersConfig,
              [providerId]: {
                ...state.ttsProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        setASRProviderConfig: (providerId, config) =>
          set((state) => ({
            asrProvidersConfig: {
              ...state.asrProvidersConfig,
              [providerId]: {
                ...state.asrProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        // PDF actions
        setPDFProvider: (providerId) => set({ pdfProviderId: providerId }),

        setPDFProviderConfig: (providerId, config) =>
          set((state) => ({
            pdfProvidersConfig: {
              ...state.pdfProvidersConfig,
              [providerId]: {
                ...state.pdfProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        // Image Generation actions
        setImageProvider: (providerId) => set({ imageProviderId: providerId }),
        setImageModelId: (modelId) => set({ imageModelId: modelId }),

        setImageProviderConfig: (providerId, config) =>
          set((state) => ({
            imageProvidersConfig: {
              ...state.imageProvidersConfig,
              [providerId]: {
                ...state.imageProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        // Video Generation actions
        setVideoProvider: (providerId) => set({ videoProviderId: providerId }),
        setVideoModelId: (modelId) => set({ videoModelId: modelId }),

        setVideoProviderConfig: (providerId, config) =>
          set((state) => ({
            videoProvidersConfig: {
              ...state.videoProvidersConfig,
              [providerId]: {
                ...state.videoProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        // Media generation toggle actions
        setImageGenerationEnabled: (enabled) => {
          // 【v0.x — 2026-07-12】admin hard-lock 守卫: 后台关闭「图片生成」时,
          // user 无法在设置里重新打开. 静默拒绝 (不抛错) 避免破坏点击流.
          if (enabled && !get().adminImageGenerationEnabled) {
            log.warn('[settings] setImageGenerationEnabled(true) blocked: admin image_generation disabled');
            return;
          }
          if (enabled) {
            const cfg = get().imageProvidersConfig;
            const hasUsable = Object.values(cfg).some((c) => c.isServerConfigured || c.apiKey);
            if (!hasUsable) return;
          }
          set({ imageGenerationEnabled: enabled });
        },
        setVideoGenerationEnabled: (enabled) => {
          if (enabled) {
            const cfg = get().videoProvidersConfig;
            const hasUsable = Object.values(cfg).some((c) => c.isServerConfigured || c.apiKey);
            if (!hasUsable) return;
          }
          set({ videoGenerationEnabled: enabled });
        },
        setTTSEnabled: (enabled) => set({ ttsEnabled: enabled }),
        setASREnabled: (enabled) => set({ asrEnabled: enabled }),

        // Custom audio provider actions
        addCustomTTSProvider: (id, name, baseUrl, requiresApiKey, defaultModel) =>
          set((state) => ({
            ttsProvidersConfig: {
              ...state.ttsProvidersConfig,
              [id]: {
                apiKey: '',
                baseUrl: '',
                enabled: true,
                modelId: defaultModel || '',
                customName: name,
                customDefaultBaseUrl: baseUrl,
                customVoices: [],
                isBuiltIn: false,
                requiresApiKey,
              },
            },
            ttsProviderId: id,
          })),

        removeCustomTTSProvider: (id) =>
          set((state) => {
            if (!isCustomTTSProvider(id)) return state;
            const { [id]: _, ...rest } = state.ttsProvidersConfig;
            return {
              ttsProvidersConfig: rest as typeof state.ttsProvidersConfig,
              ...(state.ttsProviderId === id && {
                ttsProviderId: 'browser-native-tts' as TTSProviderId,
                ttsVoice: 'default',
              }),
            };
          }),

        addCustomASRProvider: (id, name, baseUrl, requiresApiKey) =>
          set((state) => ({
            asrProvidersConfig: {
              ...state.asrProvidersConfig,
              [id]: {
                apiKey: '',
                baseUrl: '',
                enabled: true,
                modelId: '',
                customModels: [],
                customName: name,
                customDefaultBaseUrl: baseUrl,
                isBuiltIn: false,
                requiresApiKey,
              },
            },
            asrProviderId: id,
          })),

        removeCustomASRProvider: (id) =>
          set((state) => {
            if (!isCustomASRProvider(id)) return state;
            const { [id]: _, ...rest } = state.asrProvidersConfig;
            return {
              asrProvidersConfig: rest as typeof state.asrProvidersConfig,
              ...(state.asrProviderId === id && {
                asrProviderId: 'browser-native' as ASRProviderId,
                asrLanguage: 'zh',
              }),
            };
          }),

        // Web Search actions
        setWebSearchProvider: (providerId) => set({ webSearchProviderId: providerId }),
        setWebSearchProviderConfig: (providerId, config) =>
          set((state) => ({
            webSearchProvidersConfig: {
              ...state.webSearchProvidersConfig,
              [providerId]: {
                ...state.webSearchProvidersConfig[providerId],
                ...config,
              },
            },
          })),

        // Fetch server-configured providers and merge into local state
        fetchServerProviders: async () => {
          try {
            const res = await fetch('/api/server-providers');
            if (!res.ok) return;
            const data = (await res.json()) as {
              providers: Record<string, { models?: string[]; baseUrl?: string }>;
              tts: Record<string, { baseUrl?: string }>;
              asr: Record<string, { baseUrl?: string; priority?: number }>; // [2026-06-22] priority 字段
              pdf: Record<string, { baseUrl?: string }>;
              image: Record<string, { baseUrl?: string }>;
              video: Record<string, { baseUrl?: string }>;
              webSearch: Record<string, { baseUrl?: string }>;
            };

            set((state) => {
              // Merge LLM providers
              const newProvidersConfig = { ...state.providersConfig };
              // First reset all server flags
              for (const pid of Object.keys(newProvidersConfig)) {
                const key = pid as ProviderId;
                if (newProvidersConfig[key]) {
                  newProvidersConfig[key] = {
                    ...newProvidersConfig[key],
                    isServerConfigured: false,
                    serverModels: undefined,
                    serverBaseUrl: undefined,
                  };
                }
              }
              // Set flags for server-configured providers
              for (const [pid, info] of Object.entries(data.providers)) {
                const key = pid as ProviderId;
                if (newProvidersConfig[key]) {
                  const currentModels = newProvidersConfig[key].models;
                  // When server specifies allowed models, filter the models list
                  // while preserving custom IDs from env/YAML in server order.
                  const currentModelMap = new Map(currentModels.map((m) => [m.id, m]));
                  const filteredModels = info.models?.length
                    ? info.models.map((id) => currentModelMap.get(id) ?? { id, name: id })
                    : currentModels;
                  newProvidersConfig[key] = {
                    ...newProvidersConfig[key],
                    isServerConfigured: true,
                    serverModels: info.models,
                    serverBaseUrl: info.baseUrl,
                    models: filteredModels,
                  };
                }
              }

              // Merge TTS providers
              const newTTSConfig = { ...state.ttsProvidersConfig };
              for (const pid of Object.keys(newTTSConfig)) {
                const key = pid as TTSProviderId;
                if (newTTSConfig[key]) {
                  newTTSConfig[key] = {
                    ...newTTSConfig[key],
                    isServerConfigured: false,
                    serverBaseUrl: undefined,
                  };
                }
              }
              for (const [pid, info] of Object.entries(data.tts)) {
                const key = pid as TTSProviderId;
                if (newTTSConfig[key]) {
                  newTTSConfig[key] = {
                    ...newTTSConfig[key],
                    isServerConfigured: true,
                    serverBaseUrl: info.baseUrl,
                  };
                }
              }

              // Merge ASR providers
              const newASRConfig = { ...state.asrProvidersConfig };
              for (const pid of Object.keys(newASRConfig)) {
                const key = pid as ASRProviderId;
                if (newASRConfig[key]) {
                  newASRConfig[key] = {
                    ...newASRConfig[key],
                    isServerConfigured: false,
                    serverBaseUrl: undefined,
                  };
                }
              }
              for (const [pid, info] of Object.entries(data.asr)) {
                const key = pid as ASRProviderId;
                if (newASRConfig[key]) {
                  newASRConfig[key] = {
                    ...newASRConfig[key],
                    isServerConfigured: true,
                    serverBaseUrl: info.baseUrl,
                  };
                }
              }

              // Merge PDF providers
              const newPDFConfig = { ...state.pdfProvidersConfig };
              for (const pid of Object.keys(newPDFConfig)) {
                const key = pid as PDFProviderId;
                if (newPDFConfig[key]) {
                  newPDFConfig[key] = {
                    ...newPDFConfig[key],
                    isServerConfigured: false,
                    serverBaseUrl: undefined,
                  };
                }
              }
              for (const [pid, info] of Object.entries(data.pdf)) {
                const key = pid as PDFProviderId;
                if (newPDFConfig[key]) {
                  newPDFConfig[key] = {
                    ...newPDFConfig[key],
                    isServerConfigured: true,
                    serverBaseUrl: info.baseUrl,
                  };
                }
              }

              // Merge Image providers
              const newImageConfig = { ...state.imageProvidersConfig };
              for (const pid of Object.keys(newImageConfig)) {
                const key = pid as ImageProviderId;
                if (newImageConfig[key]) {
                  newImageConfig[key] = {
                    ...newImageConfig[key],
                    isServerConfigured: false,
                    serverBaseUrl: undefined,
                  };
                }
              }
              for (const [pid, info] of Object.entries(data.image)) {
                const key = pid as ImageProviderId;
                if (newImageConfig[key]) {
                  newImageConfig[key] = {
                    ...newImageConfig[key],
                    isServerConfigured: true,
                    serverBaseUrl: info.baseUrl,
                  };
                }
              }

              // Merge Video providers
              const newVideoConfig = { ...state.videoProvidersConfig };
              for (const pid of Object.keys(newVideoConfig)) {
                const key = pid as VideoProviderId;
                if (newVideoConfig[key]) {
                  newVideoConfig[key] = {
                    ...newVideoConfig[key],
                    isServerConfigured: false,
                    serverBaseUrl: undefined,
                  };
                }
              }
              if (data.video) {
                for (const [pid, info] of Object.entries(data.video)) {
                  const key = pid as VideoProviderId;
                  if (newVideoConfig[key]) {
                    newVideoConfig[key] = {
                      ...newVideoConfig[key],
                      isServerConfigured: true,
                      serverBaseUrl: info.baseUrl,
                    };
                  }
                }
              }

              // Merge Web Search config — reset all first, then mark server-configured
              const newWebSearchConfig = { ...state.webSearchProvidersConfig };
              for (const key of Object.keys(newWebSearchConfig) as WebSearchProviderId[]) {
                newWebSearchConfig[key] = {
                  ...newWebSearchConfig[key],
                  isServerConfigured: false,
                  serverBaseUrl: undefined,
                };
              }
              if (data.webSearch) {
                for (const [pid, info] of Object.entries(data.webSearch)) {
                  const key = pid as WebSearchProviderId;
                  if (newWebSearchConfig[key]) {
                    newWebSearchConfig[key] = {
                      ...newWebSearchConfig[key],
                      isServerConfigured: true,
                      serverBaseUrl: info.baseUrl,
                    };
                  }
                }
              }

              // === Validate current selections against updated configs ===
              // Build fallback: server-configured first, then client-key-only
              // Defensive: c may be undefined in edge cases (state not yet hydrated,
              // localStorage corruption). Use `c?.` instead of `c.` to avoid the
              // "Cannot read properties of undefined (reading 'isServerConfigured')"
              // crash that breaks /dongniai/ page rendering on first load.
              const buildFallback = <T extends string>(
                config: Record<string, { isServerConfigured?: boolean; apiKey?: string } | undefined>,
              ): T[] => [
                ...Object.entries(config)
                  .filter(([, c]) => !!c?.isServerConfigured)
                  .map(([id]) => id as T),
                ...Object.entries(config)
                  .filter(([, c]) => !c?.isServerConfigured && !!c?.apiKey)
                  .map(([id]) => id as T),
              ];

              const llmFallback = buildFallback<ProviderId>(newProvidersConfig);
              const ttsFallback = buildFallback<TTSProviderId>(newTTSConfig);
              const asrFallback = buildFallback<ASRProviderId>(newASRConfig);
              const pdfFallback = buildFallback<PDFProviderId>(newPDFConfig);
              const imageFallback = buildFallback<ImageProviderId>(newImageConfig);
              const videoFallback = buildFallback<VideoProviderId>(newVideoConfig);

              const validLLMProvider = validateProvider(
                state.providerId,
                newProvidersConfig,
                llmFallback,
              );
              const validTTSProvider = validateProvider(
                state.ttsProviderId,
                newTTSConfig,
                ttsFallback,
                'browser-native-tts' as TTSProviderId,
              );
              const validASRProvider = validateProvider(
                state.asrProviderId,
                newASRConfig,
                asrFallback,
                'browser-native' as ASRProviderId,
              );
              const validPDFProvider = validateProvider(
                state.pdfProviderId,
                newPDFConfig,
                pdfFallback,
                'unpdf' as PDFProviderId,
              );
              let validImageProvider = validateProvider(
                state.imageProviderId,
                newImageConfig,
                imageFallback,
              );
              let validVideoProvider = validateProvider(
                state.videoProviderId,
                newVideoConfig,
                videoFallback,
              );

              // Auto-recover: when provider is empty but server has available ones
              let recoveredImageModel = '';
              if (!validImageProvider && imageFallback.length > 0) {
                validImageProvider = imageFallback[0];
                const models = IMAGE_PROVIDERS[validImageProvider as ImageProviderId]?.models;
                if (models?.length) recoveredImageModel = models[0].id;
              }
              let recoveredVideoModel = '';
              if (!validVideoProvider && videoFallback.length > 0) {
                validVideoProvider = videoFallback[0];
                const models = VIDEO_PROVIDERS[validVideoProvider as VideoProviderId]?.models;
                if (models?.length) recoveredVideoModel = models[0].id;
              }

              const validLLMModel = validLLMProvider
                ? validateModel(
                    state.modelId,
                    newProvidersConfig[validLLMProvider as ProviderId]?.models ?? [],
                  )
                : '';
              const imageModels =
                IMAGE_PROVIDERS[validImageProvider as ImageProviderId]?.models ?? [];
              const validImageModel = validImageProvider
                ? recoveredImageModel ||
                  validateModel(state.imageModelId, imageModels) ||
                  // validateModel('', ...) returns '' — fallback to first model when modelId is empty
                  imageModels[0]?.id ||
                  ''
                : '';
              const videoModels =
                VIDEO_PROVIDERS[validVideoProvider as VideoProviderId]?.models ?? [];
              const validVideoModel = validVideoProvider
                ? recoveredVideoModel ||
                  validateModel(state.videoModelId, videoModels) ||
                  videoModels[0]?.id ||
                  ''
                : '';

              const validTTSVoice =
                validTTSProvider !== state.ttsProviderId
                  ? DEFAULT_TTS_VOICES[validTTSProvider as BuiltInTTSProviderId] || 'default'
                  : state.ttsVoice;

              // Auto-disable image/video generation when no provider is usable
              const shouldDisableImage = !validImageProvider && state.imageGenerationEnabled;
              const shouldDisableVideo = !validVideoProvider && state.videoGenerationEnabled;

              // === Auto-select / auto-enable (only on first run) ===
              let autoTtsProvider: TTSProviderId | undefined;
              let autoTtsVoice: string | undefined;
              let autoAsrProvider: ASRProviderId | undefined;
              let autoPdfProvider: PDFProviderId | undefined;
              let autoImageProvider: ImageProviderId | undefined;
              let autoImageModel: string | undefined;
              let autoVideoProvider: VideoProviderId | undefined;
              let autoVideoModel: string | undefined;
              let autoImageEnabled: boolean | undefined;
              let autoVideoEnabled: boolean | undefined;

              if (!state.autoConfigApplied) {
                // PDF: unpdf → mineru-cloud or mineru if server has it
                if (state.pdfProviderId === 'unpdf') {
                  if (newPDFConfig['mineru-cloud']?.isServerConfigured) {
                    autoPdfProvider = 'mineru-cloud' as PDFProviderId;
                  } else if (newPDFConfig.mineru?.isServerConfigured) {
                    autoPdfProvider = 'mineru' as PDFProviderId;
                  }
                }

                // TTS: select first server provider if current is not server-configured
                const serverTtsIds = Object.keys(data.tts) as TTSProviderId[];
                if (
                  serverTtsIds.length > 0 &&
                  !newTTSConfig[state.ttsProviderId]?.isServerConfigured
                ) {
                  autoTtsProvider = serverTtsIds[0];
                  autoTtsVoice =
                    DEFAULT_TTS_VOICES[autoTtsProvider as BuiltInTTSProviderId] || 'default';
                }

                // ASR: select first server provider (by admin priority ASC) if current is not server-configured
                // 【2026-06-22 老板新规】按 fa_dongniai_provider_config.priority ASC 排序选，
                //   之前用 Object.keys() 字母序会选到 priority 较低但 endpoint 不存在的小米 ASR。
                const serverAsrIds = (Object.keys(data.asr) as ASRProviderId[]).sort(
                  (a, b) => (data.asr[a]?.priority ?? 999) - (data.asr[b]?.priority ?? 999)
                );
                if (
                  serverAsrIds.length > 0 &&
                  !newASRConfig[state.asrProviderId]?.isServerConfigured
                ) {
                  autoAsrProvider = serverAsrIds[0];
                }

                // Image: first server provider
                const serverImageIds = Object.keys(data.image) as ImageProviderId[];
                if (
                  serverImageIds.length > 0 &&
                  !newImageConfig[state.imageProviderId]?.isServerConfigured
                ) {
                  autoImageProvider = serverImageIds[0];
                  const models = IMAGE_PROVIDERS[autoImageProvider]?.models;
                  if (models?.length) autoImageModel = models[0].id;
                }
                if (serverImageIds.length > 0 && !state.imageGenerationEnabled && state.adminImageGenerationEnabled) {
                  autoImageEnabled = true;
                }

                // Video: first server provider
                const serverVideoIds = Object.keys(data.video || {}) as VideoProviderId[];
                if (
                  serverVideoIds.length > 0 &&
                  !newVideoConfig[state.videoProviderId]?.isServerConfigured
                ) {
                  autoVideoProvider = serverVideoIds[0];
                  const models = VIDEO_PROVIDERS[autoVideoProvider]?.models;
                  if (models?.length) autoVideoModel = models[0].id;
                }
                if (serverVideoIds.length > 0 && !state.videoGenerationEnabled) {
                  autoVideoEnabled = true;
                }
              }

              // LLM auto-select: only on true first load (no provider selected yet)
              let autoProviderId: ProviderId | undefined;
              let autoModelId: string | undefined;
              if (!state.providerId && !state.modelId) {
                for (const [pid, cfg] of Object.entries(newProvidersConfig)) {
                  if (cfg.isServerConfigured) {
                    // Prefer server-restricted models, fall back to built-in list
                    const serverModels = cfg.serverModels;
                    const modelId = serverModels?.length
                      ? serverModels[0]
                      : PROVIDERS[pid as ProviderId]?.models[0]?.id;
                    if (modelId) {
                      autoProviderId = pid as ProviderId;
                      autoModelId = modelId;
                      break;
                    }
                  }
                }
              }

              return {
                providersConfig: newProvidersConfig,
                ttsProvidersConfig: newTTSConfig,
                asrProvidersConfig: newASRConfig,
                pdfProvidersConfig: newPDFConfig,
                imageProvidersConfig: newImageConfig,
                videoProvidersConfig: newVideoConfig,
                webSearchProvidersConfig: newWebSearchConfig,
                autoConfigApplied: true,
                // Validated selections
                ...(validLLMProvider !== state.providerId && {
                  providerId: validLLMProvider as ProviderId,
                }),
                ...(validLLMModel !== state.modelId && { modelId: validLLMModel }),
                ...(validTTSProvider !== state.ttsProviderId && {
                  ttsProviderId: validTTSProvider as TTSProviderId,
                  ttsVoice: validTTSVoice,
                }),
                ...(validASRProvider !== state.asrProviderId && {
                  asrProviderId: validASRProvider as ASRProviderId,
                }),
                ...(validPDFProvider !== state.pdfProviderId && {
                  pdfProviderId: validPDFProvider as PDFProviderId,
                }),
                ...(validImageProvider !== state.imageProviderId && {
                  imageProviderId: validImageProvider as ImageProviderId,
                }),
                ...(validImageModel !== state.imageModelId && {
                  imageModelId: validImageModel,
                }),
                ...(validVideoProvider !== state.videoProviderId && {
                  videoProviderId: validVideoProvider as VideoProviderId,
                }),
                ...(validVideoModel !== state.videoModelId && {
                  videoModelId: validVideoModel,
                }),
                ...(shouldDisableImage && { imageGenerationEnabled: false }),
                ...(shouldDisableVideo && { videoGenerationEnabled: false }),
                // First-run auto-select overrides validation (autoConfigApplied guard).
                // On first sync, auto-select picks the best provider. On subsequent syncs,
                // auto* variables stay undefined so only validation spreads take effect.
                ...(autoPdfProvider && { pdfProviderId: autoPdfProvider }),
                ...(autoTtsProvider && {
                  ttsProviderId: autoTtsProvider,
                  ttsVoice: autoTtsVoice,
                }),
                ...(autoAsrProvider && { asrProviderId: autoAsrProvider }),
                ...(autoImageProvider && {
                  imageProviderId: autoImageProvider,
                }),
                ...(autoImageModel && { imageModelId: autoImageModel }),
                ...(autoVideoProvider && {
                  videoProviderId: autoVideoProvider,
                }),
                ...(autoVideoModel && { videoModelId: autoVideoModel }),
                ...(autoImageEnabled !== undefined && {
                  imageGenerationEnabled: autoImageEnabled,
                }),
                ...(autoVideoEnabled !== undefined && {
                  videoGenerationEnabled: autoVideoEnabled,
                }),
                ...(autoProviderId && { providerId: autoProviderId }),
                ...(autoModelId && { modelId: autoModelId }),
              };
            });
          } catch (e) {
            // Silently fail — server providers are optional
            log.warn('Failed to fetch server providers:', e);
          }
        },

        fetchAdminDefaults: async (force: boolean = false) => {
          // 【v6.97.50.3 P0 FIX 2026-07-18】模块级单例 dedupe + in-flight 锁 + 最小间隔 throttle
          // 真因：老板浏览器看到 /api/config/defaults 40ms 内连发 40 次
          //   3 个组件 (home + classroom/[id] + generation-preview) 各自 mount 时 useEffect 调一次
          //   + useConfigVersionPolling 的 1s interval 又调一次
          //   + React 18 strict mode mount/unmount/mount 双触发
          //   → 4-8 个并发请求同时打后端 → 后端撞 /config/defaults → 反代冲 PHP → 504 雪崩
          //   → 浏览器主线程被 fetch callback 排满 → "进入课堂" click handler 永远进不了 microtask
          //   → 用户看到"半天没反应"
          // 修法 3 重：
          //   ① module-level inFlightAdminDefaultsPromise：同一时刻所有 caller 共享同一个 Promise
          //   ② module-level lastAdminDefaultsSuccessAt：成功后 1500ms 内重复调用直接 return
          //   ③ module-level lastAdminDefaultsFailureAt：失败后 3000ms 内重复调用直接 throw（让退避生效）
          // 【2026-09-04 P0】支持 force=true 跳过 throttle（useConfigVersionPolling 在版本变化后立刻调用），
          //   但仍保留 in-flight 去重（同一时刻共享 Promise），不会产生 504 雪崩
          if (inFlightAdminDefaultsPromise) {
            return inFlightAdminDefaultsPromise;
          }
          const _now = Date.now();
          if (!force && _now - lastAdminDefaultsSuccessAt < ADMIN_DEFAULTS_MIN_INTERVAL_MS) {
            return;
          }
          if (
            !force &&
            lastAdminDefaultsFailureAt > 0 &&
            _now - lastAdminDefaultsFailureAt < ADMIN_DEFAULTS_FAILURE_BACKOFF_MS
          ) {
            throw new Error(
              `config/defaults throttled (last failure ${_now - lastAdminDefaultsFailureAt}ms ago)`
            );
          }
          let resolveOuter: () => void = () => {};
          const promise: Promise<void> = new Promise<void>((resolve) => {
            resolveOuter = resolve;
          });
          inFlightAdminDefaultsPromise = promise;
          (async () => {
            try {
            // 2026-07-08 P0 修复：不再跨域 fetch ceshi.xiaoquanwk.com（会触发 CORS + Chrome loopback 拦截）
            // 改调 Next.js 自己的 API 路由（/dongniai/api/config/defaults），由服务端去 fetch PHP
            // 这样跟当前访问域名（ceshi / www）自动一致，不需要硬编码域名
            const res = await fetch('/dongniai/api/config/defaults');
            // 【v24 P0 FIX 2026-07-11】throw 而非 silent return，让 caller 能感知 5xx 并触发指数退避
            // 真因：之前 `if (!res.ok) return;` 让 fetchAdminDefaults 永远不 throw
            //       → useConfigVersionPolling 的 catch 块永远捕不到 → 504 死循环
            // 移植 v13 commit 5c4d66f4 模式，配套 use-config-version-polling.ts 退避逻辑
            if (!res.ok) {
              throw new Error(`config/defaults HTTP ${res.status} ${res.statusText}`);
            }
            const json = (await res.json()) as {
              success: boolean;
              data: Record<string, unknown> & {
                // Root-level media feature toggles (back-compat)
                imageGenerationEnabled?: boolean;
                videoGenerationEnabled?: boolean;
                ttsEnabled?: boolean;
                asrEnabled?: boolean;
              };
            };
            if (!json?.data) return;

            // ============================================================
            // 【2026-06-08 修复】Normalize PHP `defaultConfig` 响应
            //
            // 老板规则 v2 §A: "最近添加的配置 → 默认 → 开启 → 优先级最小".
            // PHP 端 `defaultConfig` 现在返 ARRAY of entries (按 priority ASC 排序),
            // 但本函数下面 L1595-1900 的所有 7 个能力处理逻辑都假设 `data.<cat>` 是
            // 单个 OBJECT（用 .provider / .models / .voice 字段直接访问）。
            //
            // 当 PHP 改成 array 后, 旧代码用 `defaults.tts.models` 等访问会得到
            // undefined (array 没有这些属性), 整个 set() 块 catch 后被吞, store
            // 不更新 — 这就是"后台改了配置前端 UI 看不到实时同步"的根因。
            //
            // 修法: 在拿到响应后立即 normalize — 把每个 category 的 array
            // 按 priority ASC + configId DESC (最近添加) 排序, 取 [0]。
            // 这样下面 7 段代码不需要任何改动, 全部按"§A 选中那条"处理。
            // ============================================================
            const ALL_CATEGORIES = ['llm', 'image', 'video', 'tts', 'asr', 'search', 'pdf', 'ocr'] as const;
            type CategoryEntry = {
              id?: string;
              name?: string;
              provider?: string;
              apiKey?: string;
              baseUrl?: string;
              models?: string;
              voice?: string;
              priority?: number;
              configId?: number;
              extraConfig?: unknown;
            };
            const pickFirstByPriority = (arr: CategoryEntry[]): CategoryEntry | null => {
              if (!Array.isArray(arr) || arr.length === 0) return null;
              const sorted = [...arr].sort((a, b) => {
                const pa = typeof a.priority === 'number' ? a.priority : 999;
                const pb = typeof b.priority === 'number' ? b.priority : 999;
                if (pa !== pb) return pa - pb;
                // tie-break: most-recently-added wins (larger configId first)
                return (b.configId ?? 0) - (a.configId ?? 0);
              });
              return sorted[0];
            };
            const normalized: Record<string, CategoryEntry | null> = {};
            for (const cat of ALL_CATEGORIES) {
              const v = json.data[cat];
              if (Array.isArray(v)) {
                normalized[cat] = pickFirstByPriority(v as CategoryEntry[]);
              } else if (v && typeof v === 'object') {
                // Backward compat: some legacy code paths still return single object
                normalized[cat] = v as CategoryEntry;
              } else {
                normalized[cat] = null;
              }
            }
            // Re-assemble the `defaults` object that the rest of this function expects.
            // The original `json.data` may also contain root-level fields
            // (imageGenerationEnabled etc.) — preserve them.
            const defaults: any = {
              ...normalized,
              imageGenerationEnabled: json.data.imageGenerationEnabled,
              videoGenerationEnabled: json.data.videoGenerationEnabled,
              ttsEnabled: json.data.ttsEnabled,
              asrEnabled: json.data.asrEnabled,
            };

            // Normalize provider name for comparison (Chinese/English parentheses map to same)
            const norm = (s: string) =>
              s.replace(/[（()）]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

            // Check if normalized string `n` is contained in `container` (or vice versa)
            // NOTE: We check both directions because short names like "minimax" should match long names like "MiniMax TTS"
            const contains = (n: string, container: string): boolean => {
              if (!n || !container) return false;
              const cn = norm(container);
              return cn.includes(n) || n.includes(cn) || containsWord(n, cn);
            };
            
            // Check if words in `n` are all present in `cn`
            const containsWord = (n: string, cn: string): boolean => {
              if (!n || !cn) return false;
              const nWords = n.split(/\s+/);
              const cnWords = cn.split(/\s+/);
              return nWords.every(w => cnWords.some(cw => cw.includes(w) || w.includes(cw)));
            };

            set((current) => {
              // Clone all configs (fallback to empty object to survive partial init states)
              const llmConfig = { ...(current.providersConfig || {}) };
              const ttsConfig = { ...(current.ttsProvidersConfig || {}) };
              const asrConfig = { ...(current.asrProvidersConfig || {}) };
              const imgConfig = { ...(current.imageProvidersConfig || {}) };
              const vidConfig = { ...(current.videoProvidersConfig || {}) };
              const pdfConfig = { ...(current.pdfProvidersConfig || {}) };
              const wsConfig = { ...(current.webSearchProvidersConfig || {}) };

              // Helper: match by provider name or id (exact + partial)
              const findMatch = (
                name: string,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                map: any,
              ): string | null => {
                if (!map || typeof map !== 'object') return null;
                const n = norm(name);
                for (const [id, cfg] of Object.entries(map as Record<string, { name?: string }>)) {
                  if (cfg && (cfg.name === name || id === name)) return id;
                  if (cfg && (norm(cfg.name || '') === n || norm(id) === n)) return id;
                  // Partial match: admin provider name partially contains/exists in frontend name
                  if (cfg && (contains(n, cfg.name || '') || contains(n, id))) return id;
                }
                return null;
              };

              // Helper: apply config to a matched provider (null-safe)
              const applyCfg = <T extends object>(
                cfg: T | undefined,
                pid: string,
                apiKey: string,
                baseUrl: string,
              ): T => {
                const c = cfg as Record<string, unknown>;
                if (!c) return cfg as T;
                c.apiKey = apiKey;
                c.baseUrl = baseUrl;
                c.enabled = true;
                c.isServerConfigured = true;
                return cfg as T;
              };

              // Collect all config updates and apply at the end (no early return)
              const updates: Partial<SettingsState> = {};

              // LLM — constant-based matching FIRST (same pattern as TTS/ASR/Image/Video/PDF/WebSearch),
              // with fallback to findMatch on llmConfig for custom providers not in the built-in registry.
              if (defaults.llm) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.llm.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(PROVIDERS)) {
                      if (pid === dn || norm(cfg.name || '') === dn) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  if (!matched) {
                    matched = findMatch(dbProviderName, llmConfig);
                  }
                  if (matched) {
                    const adminApiKey = defaults.llm.apiKey || '';
                    const adminBaseUrl = defaults.llm.baseUrl || '';
                    const adminModels = defaults.llm.models
                      ? defaults.llm.models.split(',').map((m: string) => m.trim())
                      : [];
                    const existing = (llmConfig as Record<string, object>)[matched] as Record<string, unknown>;
                    (llmConfig as Record<string, object>)[matched] = {
                      ...existing,
                      apiKey: adminApiKey,
                      baseUrl: adminBaseUrl,
                      isServerConfigured: true,
                      serverModels: adminModels,
                      serverBaseUrl: adminBaseUrl,
                      models:
                        adminModels.length > 0 && adminModels[0] !== ''
                          ? adminModels.map((id: string) => ({ id, name: id }))
                          : (llmConfig as Record<string, { models: { id: string; name: string }[] }>)[matched]?.models ?? [],
                    };
                    Object.assign(updates, {
                      providersConfig: llmConfig,
                      providerId: matched as ProviderId,
                      modelId: (adminModels[0] && adminModels[0] !== '') ? adminModels[0] : ((llmConfig as Record<string, { models: { id: string; name: string }[] }>)[matched]?.models?.[0]?.id || current.modelId),
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] LLM config apply failed:', e);
                }
              }

              // TTS
              if (defaults.tts) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.tts.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(TTS_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on ttsProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, ttsConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (ttsConfig as Record<string, unknown>)[matched] = applyCfg(
                      (ttsConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.tts.apiKey || '',
                      defaults.tts.baseUrl || '',
                    );
                    const newTtsProviderId = matched as TTSProviderId;
                    // Inject custom voices from admin config (extraConfig.voices)
                    // PHP returns {value, label, type} → map to {id, name}
                    const voices = (defaults.tts as { extraConfig?: { voices?: Array<{ value: string; label: string; type?: string }> } } | null)?.extraConfig?.voices;
                    if (voices && voices.length > 0) {
                      const voiceEntry = ((ttsConfig as Record<string, Record<string, unknown>>)[matched]) || {};
                      voiceEntry.customVoices = voices.map(v => ({ id: v.value, name: v.label }));
                      (ttsConfig as Record<string, unknown>)[matched] = voiceEntry;
                    }
                    const defaultVoice =
                      (defaults.tts as { voice?: string } | null)?.voice
                      || (voices && voices.length > 0 ? voices[0].value : DEFAULT_TTS_VOICES[newTtsProviderId as keyof typeof DEFAULT_TTS_VOICES])
                      || 'default';
                    // Per spec: validate the user-selected voice against the
                    // (possibly new) provider's valid voice list. If it's no
                    // longer valid — e.g. admin switched the default TTS
                    // provider, removed the user's voice from the voice list,
                    // or the user picked something that never belonged here —
                    // treat the selection as "user hasn't picked anything" and
                    // use the role default voice instead.
                    let finalVoice: string;
                    let finalUserSelectedVoice: boolean;
                    if (current.userSelectedVoice) {
                      const validVoices = getValidVoicesForProvider(
                        newTtsProviderId,
                        ttsConfig as Record<string, { customVoices?: Array<{ id: string; name?: string }> }>,
                      );
                      if (validVoices.includes(current.ttsVoice)) {
                        finalVoice = current.ttsVoice;
                        finalUserSelectedVoice = true;
                      } else {
                        log.warn(
                          `[fetchAdminDefaults] User-selected voice "${current.ttsVoice}" not valid for provider "${newTtsProviderId}". Falling back to default voice "${defaultVoice}".`,
                        );
                        finalVoice = defaultVoice;
                        finalUserSelectedVoice = false;
                      }
                    } else {
                      finalVoice = defaultVoice;
                      finalUserSelectedVoice = false;
                    }
                    Object.assign(updates, {
                      ttsProviderId: newTtsProviderId,
                      ttsVoice: finalVoice,
                      userSelectedVoice: finalUserSelectedVoice,
                      ttsProvidersConfig: ttsConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] TTS config apply failed:', e);
                }
              }

              // ASR
              if (defaults.asr) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.asr.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(ASR_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on asrProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, asrConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (asrConfig as Record<string, unknown>)[matched] = applyCfg(
                      (asrConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.asr.apiKey || '',
                      defaults.asr.baseUrl || '',
                    );
                    Object.assign(updates, {
                      asrProviderId: matched as ASRProviderId,
                      asrProvidersConfig: asrConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] ASR config apply failed:', e);
                }
              }

              // Image
              if (defaults.image) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.image.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(IMAGE_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on imageProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, imgConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (imgConfig as Record<string, unknown>)[matched] = applyCfg(
                      (imgConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.image.apiKey || '',
                      defaults.image.baseUrl || '',
                    );
                    Object.assign(updates, {
                      imageProviderId: matched as ImageProviderId,
                      imageGenerationEnabled: true,
                      imageProvidersConfig: imgConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] Image config apply failed:', e);
                }
              }

              // Video
              if (defaults.video) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.video.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(VIDEO_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on videoProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, vidConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (vidConfig as Record<string, unknown>)[matched] = applyCfg(
                      (vidConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.video.apiKey || '',
                      defaults.video.baseUrl || '',
                    );
                    Object.assign(updates, {
                      videoProviderId: matched as VideoProviderId,
                      videoGenerationEnabled: true,
                      videoProvidersConfig: vidConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] Video config apply failed:', e);
                }
              }

              // PDF
              if (defaults.pdf) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.pdf.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(PDF_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on pdfProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, pdfConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (pdfConfig as Record<string, unknown>)[matched] = applyCfg(
                      (pdfConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.pdf.apiKey || '',
                      defaults.pdf.baseUrl || '',
                    );
                    Object.assign(updates, {
                      pdfProviderId: matched as PDFProviderId,
                      pdfProvidersConfig: pdfConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] PDF config apply failed:', e);
                }
              }

              // Web Search
              if (defaults.search) {
                try {
                  let matched: string | null = null;
                  const dbProviderName = defaults.search.provider || '';
                  const dn = norm(dbProviderName);
                  if (dn) {
                    for (const [pid, cfg] of Object.entries(WEB_SEARCH_PROVIDERS)) {
                      const cn = norm(cfg.name || '');
                      if (cn === dn || contains(dn, cfg.name || '') || contains(cfg.name || '', dn)) {
                        matched = pid;
                        break;
                      }
                    }
                  }
                  // Fallback: try findMatch on webSearchProvidersConfig
                  if (!matched) {
                    matched = findMatch(dbProviderName, wsConfig as Record<string, { name?: string }>);
                  }
                  if (matched) {
                    (wsConfig as Record<string, unknown>)[matched] = applyCfg(
                      (wsConfig as Record<string, unknown>)[matched] as object,
                      matched,
                      defaults.search.apiKey || '',
                      defaults.search.baseUrl || '',
                    );
                    Object.assign(updates, {
                      webSearchProviderId: matched as WebSearchProviderId,
                      webSearchProvidersConfig: wsConfig,
                    });
                  }
                } catch (e) {
                  log.warn('[fetchAdminDefaults] WebSearch config apply failed:', e);
                }
              }

              // 媒体功能开关（由后台管理员统一配置）
              // 【v0.x — 2026-07-12】adminLock 处理:
              //   - adminOn=true  → imageGenerationEnabled 正常同步 (按 server 字段)
              //   - adminOn=false → 强制 imageGenerationEnabled=false (即使 store 之前是 true)
              //                     + adminImageGenerationEnabled 锁住, user 无法从 UI 重新 enable
              if (defaults.imageGenerationEnabled !== undefined) {
                const adminOn = !!defaults.imageGenerationEnabled;
                Object.assign(updates, {
                  adminImageGenerationEnabled: adminOn,
                  imageGenerationEnabled: adminOn
                    ? defaults.imageGenerationEnabled
                    : false,
                });
              }
              if (defaults.videoGenerationEnabled !== undefined) {
                Object.assign(updates, { videoGenerationEnabled: defaults.videoGenerationEnabled });
              }
              if (defaults.ttsEnabled !== undefined) {
                Object.assign(updates, { ttsEnabled: defaults.ttsEnabled });
              }
              if (defaults.asrEnabled !== undefined) {
                Object.assign(updates, { asrEnabled: defaults.asrEnabled });
              }

              lastAdminDefaultsSuccessAt = Date.now();
              lastAdminDefaultsFailureAt = 0;
              return { ...current, ...updates };
            });
            } catch (e) {
              lastAdminDefaultsFailureAt = Date.now();
              log.warn('Failed to fetch admin defaults:', e);
              throw e;
            } finally {
              // 释放 in-flight 锁，让下次调用可以重新发起（受 throttle 控制）
              inFlightAdminDefaultsPromise = null;
              resolveOuter();
            }
          })();
          return promise;
        },
      };
    },
    {
      name: 'settings-storage',
      version: 2,
      storage: createJSONStorage(() => {
        // 【2026-07-01 P0 #2 修复】SSR-safe storage
        //  原因: Next.js 16 SSR 端 (Node.js) 无 window/localStorage, ReferenceError
        //        抛自 zustand persist middleware, prod log 每分钟 spam warning
        //  修法: storage 工厂 SSR 端返 noop, client 端返 safeStorage
        if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
          // SSR: noop storage (zustand skipHydration 已在 persist 配)
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {},
          };
        }
        // Gracefully handle corrupted localStorage data (circular references in TTS config)
        // without crashing — the in-memory state remains valid and operational.
        const safeStorage = {
          getItem: (name: string) => {
            try {
              return window.localStorage.getItem(name);
            } catch (e) {
              log.warn('[Settings] Failed to read state from localStorage:', e);
              return null;
            }
          },
          setItem: (name: string, value: string) => {
            try {
              window.localStorage.setItem(name, value);
            } catch (e) {
              log.warn('[Settings] Failed to write state to localStorage:', e);
            }
          },
          removeItem: (name: string) => {
            try {
              window.localStorage.removeItem(name);
            } catch (e) {
              log.warn('[Settings] Failed to remove state from localStorage:', e);
            }
          },
        };
        return safeStorage;
      }),
      // Migrate persisted state
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Partial<SettingsState>;

        // v0 → v1: clear hardcoded default model so user must actively select
        if (version === 0) {
          if (state.providerId === 'openai' && state.modelId === 'gpt-4o-mini') {
            state.modelId = '';
          }
        }

        // Ensure providersConfig has all built-in providers (also in merge below)
        ensureBuiltInProviders(state);
        promoteLegacyCustomProviderBaseUrls(state);

        // Ensure image/video configs have all built-in providers
        ensureBuiltInImageProviders(state);
        ensureBuiltInVideoProviders(state);

        // Migrate from old ttsModel to new ttsProviderId
        if (state.ttsModel && !state.ttsProviderId) {
          // Map old ttsModel values to new ttsProviderId
          if (state.ttsModel === 'openai-tts') {
            state.ttsProviderId = 'openai-tts';
          } else if (state.ttsModel === 'azure-tts') {
            state.ttsProviderId = 'azure-tts';
          } else {
            // Default to OpenAI
            state.ttsProviderId = 'openai-tts';
          }
        }

        // Add default audio config if missing
        if (!state.ttsProvidersConfig || !state.asrProvidersConfig) {
          const defaultAudioConfig = getDefaultAudioConfig();
          Object.assign(state, defaultAudioConfig);
        }
        ensureBuiltInAudioProviders(state);

        // Migrate global ttsModelId to per-provider
        if ((state as Record<string, unknown>).ttsModelId) {
          const pid = state.ttsProviderId;
          if (pid && state.ttsProvidersConfig?.[pid]) {
            state.ttsProvidersConfig[pid].modelId = (state as Record<string, unknown>)
              .ttsModelId as string;
          }
          delete (state as Record<string, unknown>).ttsModelId;
        }
        // Same for asrModelId
        if ((state as Record<string, unknown>).asrModelId) {
          const pid = state.asrProviderId;
          if (pid && state.asrProvidersConfig?.[pid]) {
            state.asrProvidersConfig[pid].modelId = (state as Record<string, unknown>)
              .asrModelId as string;
          }
          delete (state as Record<string, unknown>).asrModelId;
        }
        // Migrate MiniMax's model field to modelId
        for (const [, cfg] of Object.entries(
          (state.ttsProvidersConfig as Record<string, Record<string, unknown>>) || {},
        )) {
          if (cfg.model && !cfg.modelId) {
            cfg.modelId = cfg.model;
            delete cfg.model;
          }
        }

        // Add default PDF config if missing
        if (!state.pdfProvidersConfig) {
          const defaultPDFConfig = getDefaultPDFConfig();
          Object.assign(state, defaultPDFConfig);
        }

        // Add default Image config if missing
        if (!state.imageProvidersConfig) {
          const defaultImageConfig = getDefaultImageConfig();
          Object.assign(state, defaultImageConfig);
        }

        // Add default Video config if missing
        if (!state.videoProvidersConfig) {
          const defaultVideoConfig = getDefaultVideoConfig();
          Object.assign(state, defaultVideoConfig);
        }

        // v1 → v2: Replace deep research with web search
        if (version < 2) {
          delete (state as Record<string, unknown>).deepResearchProviderId;
          delete (state as Record<string, unknown>).deepResearchProvidersConfig;
        }

        // Add default media generation toggles if missing
        if (state.imageGenerationEnabled === undefined) {
          state.imageGenerationEnabled = false;
        }
        if (state.videoGenerationEnabled === undefined) {
          state.videoGenerationEnabled = false;
        }

        // Add default audio toggles if missing
        if ((state as Record<string, unknown>).ttsEnabled === undefined) {
          (state as Record<string, unknown>).ttsEnabled = true;
        }
        if ((state as Record<string, unknown>).asrEnabled === undefined) {
          (state as Record<string, unknown>).asrEnabled = true;
        }

        // Existing users already have their config set up — mark auto-config as done
        if ((state as Record<string, unknown>).autoConfigApplied === undefined) {
          (state as Record<string, unknown>).autoConfigApplied = true;
        }

        if ((state as Record<string, unknown>).agentMode === undefined) {
          (state as Record<string, unknown>).agentMode = 'preset';
        }
        if ((state as Record<string, unknown>).autoAgentCount === undefined) {
          (state as Record<string, unknown>).autoAgentCount = 3;
        }

        if ((state as Record<string, unknown>).thinkingConfigs === undefined) {
          (state as Record<string, unknown>).thinkingConfigs = {};
        }

        // Migrate Web Search: old flat fields → new provider-based config
        // Also ensure minimax exists (existing users may have only tavily from earlier versions)
        if (!state.webSearchProvidersConfig || !state.webSearchProvidersConfig.minimax) {
          const stateRecord = state as Record<string, unknown>;
          const oldApiKey = (stateRecord.webSearchApiKey as string) || '';
          const oldIsServerConfigured =
            (stateRecord.webSearchIsServerConfigured as boolean) || false;
          state.webSearchProviderId = 'tavily' as WebSearchProviderId;
          state.webSearchProvidersConfig = {
            tavily: {
              apiKey: oldApiKey,
              baseUrl: '',
              enabled: true,
              isServerConfigured: oldIsServerConfigured,
            },
            minimax: {
              apiKey: '',
              baseUrl: '',
              enabled: false,
              isServerConfigured: false,
            },
          } as SettingsState['webSearchProvidersConfig'];
          delete stateRecord.webSearchApiKey;
          delete stateRecord.webSearchIsServerConfigured;
        }

        ensureValidProviderSelections(state);
        ensureBuiltInAudioProviders(state);
        state.thinkingConfigs = pruneThinkingConfigs(state.thinkingConfigs, state.providersConfig);

        return state;
      },
      // Custom merge: always sync built-in providers on every rehydrate,
      // so newly added providers/models appear without clearing cache.
      merge: (persistedState, currentState) => {
        const merged = { ...currentState, ...(persistedState as object) };
        ensureBuiltInProviders(merged as Partial<SettingsState>);
        promoteLegacyCustomProviderBaseUrls(merged as Partial<SettingsState>);
        ensureBuiltInAudioProviders(merged as Partial<SettingsState>);
        ensureBuiltInImageProviders(merged as Partial<SettingsState>);
        ensureBuiltInVideoProviders(merged as Partial<SettingsState>);
        ensureValidProviderSelections(merged as Partial<SettingsState>);
        const typedMerged = merged as Partial<SettingsState>;
        typedMerged.thinkingConfigs = pruneThinkingConfigs(
          typedMerged.thinkingConfigs,
          typedMerged.providersConfig,
        );
        // Always default autoPlayLecture to true for new/old users
        merged.autoPlayLecture = true;
        return merged as SettingsState;
      },
    },
  ),
);
